import type { OpCategory } from '@wetron/tokens';

export type { OpCategory };

// Op type -> category lookup.
//
// Two maps with different normalization rules:
//
//   CATEGORY_MAP - keyed by `opType.toLowerCase().replace(/_/g, "")`. Covers ONNX
//   PascalCase ("Conv"), TFLite UPPER_SNAKE_CASE ("CONV_2D"), Keras class names
//   ("Conv2D"), and TF raw ops ("FusedBatchNormV3"). After normalization these
//   collapse to a single key - e.g. "Conv2D", "CONV_2D", "conv2d" all -> "conv2d".
//
//   ATEN_MAP - keyed by the bare op base from `opBase()` (no normalization).
//   Used for PyTorch namespace ops "aten::add", "aten::leaky_relu" - base names
//   keep their underscores ("leaky_relu") and are case-sensitive lowercase.
//
// Lookup order in `opCategory`: CATEGORY_MAP first, then ATEN_MAP via opBase,
// then "unknown". Keep both maps in sync if you add a new category.

const CATEGORY_MAP: Record<string, OpCategory> = {
  // I/O
  input: 'input',
  output: 'output',

  // Convolution
  conv: 'conv',
  convtranspose: 'conv',
  depthwiseconv: 'conv',
  qlinearconv: 'conv',
  conv1d: 'conv',
  conv2d: 'conv',
  conv3d: 'conv',
  conv1dtranspose: 'conv',
  conv2dtranspose: 'conv',
  conv3dtranspose: 'conv',
  depthwiseconv1d: 'conv',
  depthwiseconv2d: 'conv',
  separableconv1d: 'conv',
  separableconv2d: 'conv',
  transposeconv: 'conv',
  stablehloconvolution: 'conv',

  // Math / linear algebra
  gemm: 'math',
  matmul: 'math',
  linear: 'math',
  qlinearmatmul: 'math',
  fullyconnected: 'math',
  embedding: 'math',
  feedforward: 'math',
  batchmatmul: 'math',
  stablehlodotgeneral: 'math',
  dense: 'math',
  add: 'math',
  sub: 'math',
  mul: 'math',
  div: 'math',
  pow: 'math',
  sqrt: 'math',
  rsqrt: 'math',
  square: 'math',
  exp: 'math',
  log: 'math',
  abs: 'math',
  neg: 'math',
  ceil: 'math',
  floor: 'math',
  round: 'math',
  sign: 'math',
  reciprocal: 'math',
  max: 'math',
  min: 'math',
  maximum: 'math',
  minimum: 'math',
  mod: 'math',
  clip: 'math',
  addn: 'math',
  sin: 'math',
  cos: 'math',
  atan2: 'math',
  floordiv: 'math',
  floormod: 'math',
  squareddifference: 'math',
  rightshift: 'math',
  cast: 'math',
  bitcast: 'math',
  not: 'math',
  and: 'math',
  or: 'math',
  xor: 'math',
  bitshift: 'math',
  bitwiseand: 'math',
  bitwiseor: 'math',
  bitwisexor: 'math',
  bitwisenot: 'math',
  biasadd: 'math',
  addv2: 'math',
  realdiv: 'math',
  matrixbandpart: 'math',
  subtract: 'math',
  multiply: 'math',
  average: 'math',
  dot: 'math',
  stablehloadd: 'math',
  stablehlosubtract: 'math',
  stablehlomultiply: 'math',
  stablehlodivide: 'math',
  stablehlomaximum: 'math',
  stablehlominimum: 'math',
  stablehloabs: 'math',
  stablehlonegate: 'math',
  stablehlopower: 'math',
  stablehloremainder: 'math',
  stablehlorsqrt: 'math',
  stablehloand: 'math',
  stablehloor: 'math',
  stablehlocompare: 'math',
  stablehlocosine: 'math',
  stablehloexponential: 'math',
  stablehlofloor: 'math',
  stablehlolog: 'math',

  // Activation
  relu: 'activation',
  relu6: 'activation',
  relun1to1: 'activation',
  relu0to1: 'activation',
  sigmoid: 'activation',
  logistic: 'activation',
  tanh: 'activation',
  softmax: 'activation',
  logsoftmax: 'activation',
  gelu: 'activation',
  silu: 'activation',
  elu: 'activation',
  leakyrelu: 'activation',
  selu: 'activation',
  mish: 'activation',
  hardswish: 'activation',
  hardsigmoid: 'activation',
  prelu: 'activation',
  erf: 'activation',
  activation: 'activation',
  thresholdedrelu: 'activation',
  dropout: 'activation',
  stablehlologistic: 'activation',
  stablehlotanh: 'activation',

  // Normalization
  batchnormalization: 'normalization',
  layernormalization: 'normalization',
  groupnormalization: 'normalization',
  instancenormalization: 'normalization',
  lpnormalization: 'normalization',
  meanvariancenormalization: 'normalization',
  l2normalization: 'normalization',
  localresponsenormalization: 'normalization',
  unitnormalization: 'normalization',
  fusedbatchnorm: 'normalization',
  fusedbatchnormv2: 'normalization',
  fusedbatchnormv3: 'normalization',

  // Pooling
  maxpool: 'pooling',
  averagepool: 'pooling',
  globalaveragepool: 'pooling',
  globalmaxpool: 'pooling',
  lppool: 'pooling',
  maxunpool: 'pooling',
  roialign: 'pooling',
  maxpool2d: 'pooling',
  averagepool2d: 'pooling',
  l2pool2d: 'pooling',
  maxpool3d: 'pooling',
  avgpool: 'pooling',
  avgpool3d: 'pooling',
  maxpooling1d: 'pooling',
  maxpooling2d: 'pooling',
  maxpooling3d: 'pooling',
  averagepooling1d: 'pooling',
  averagepooling2d: 'pooling',
  averagepooling3d: 'pooling',
  globalmaxpooling1d: 'pooling',
  globalmaxpooling2d: 'pooling',
  globalmaxpooling3d: 'pooling',
  globalaveragepooling1d: 'pooling',
  globalaveragepooling2d: 'pooling',
  globalaveragepooling3d: 'pooling',

  // Reshape / layout
  reshape: 'reshape',
  flatten: 'reshape',
  squeeze: 'reshape',
  unsqueeze: 'reshape',
  transpose: 'reshape',
  expand: 'reshape',
  resize: 'reshape',
  upsample: 'reshape',
  spacetodepth: 'reshape',
  depthtospace: 'reshape',
  pixelshuffle: 'reshape',
  pad: 'reshape',
  padv2: 'reshape',
  mirrorpad: 'reshape',
  shape: 'reshape',
  slice: 'reshape',
  stridedslice: 'reshape',
  expanddims: 'reshape',
  broadcastto: 'reshape',
  broadcastargs: 'reshape',
  resizebilinear: 'reshape',
  resizenearestneighbor: 'reshape',
  spacetobatchnd: 'reshape',
  batchtospacend: 'reshape',
  permute: 'reshape',
  repeatvector: 'reshape',
  zeropadding1d: 'reshape',
  zeropadding2d: 'reshape',
  zeropadding3d: 'reshape',
  cropping1d: 'reshape',
  cropping2d: 'reshape',
  cropping3d: 'reshape',
  upsampling1d: 'reshape',
  upsampling2d: 'reshape',
  upsampling3d: 'reshape',
  identity: 'reshape',
  cropandresize: 'reshape',
  stablehloreshape: 'reshape',
  stablehlobroadcastindim: 'reshape',
  stablehloslice: 'reshape',

  // Constant / creation
  constant: 'constant',
  weights: 'constant',
  'gguf v2': 'constant',
  'gguf v3': 'constant',
  constantofshape: 'constant',
  const: 'constant',
  varhandleop: 'constant',
  readvariableop: 'constant',
  variablev2: 'constant',
  range: 'constant',
  fill: 'constant',
  zeroslike: 'constant',
  oneslike: 'constant',

  // Reduction
  reducemean: 'reduction',
  reducesum: 'reduction',
  reducemax: 'reduction',
  reducemin: 'reduction',
  reduceprod: 'reduction',
  reducel1: 'reduction',
  reducel2: 'reduction',
  argmax: 'reduction',
  argmin: 'reduction',
  cumsum: 'reduction',
  nonzero: 'reduction',
  mean: 'reduction',
  sum: 'reduction',
  prod: 'reduction',
  reduceany: 'reduction',
  reduceall: 'reduction',
  topkv2: 'reduction',
  segmentsum: 'reduction',
  unsortedsegmentprod: 'reduction',
  unsortedsegmentmax: 'reduction',
  unsortedsegmentsum: 'reduction',
  unsortedsegmentmin: 'reduction',
  stablehloreduce: 'reduction',
  stablehloreducewindow: 'reduction',

  // Merge / indexing / scatter
  concat: 'merge',
  concatenation: 'merge',
  concatenate: 'merge',
  concatv2: 'merge',
  split: 'merge',
  splitv: 'merge',
  gather: 'merge',
  gatherelements: 'merge',
  gathernd: 'merge',
  gatherv2: 'merge',
  tile: 'merge',
  pack: 'merge',
  unpack: 'merge',
  scatterelements: 'merge',
  scatternd: 'merge',
  where: 'merge',
  select: 'merge',
  selectv2: 'merge',
  topk: 'merge',
  onehot: 'merge',
  reversev2: 'merge',
  reversesequence: 'merge',
  dynamicupdateslice: 'merge',
  resourcegather: 'merge',
  dynamicpartition: 'merge',
  dynamicstitch: 'merge',
  stablehloscatter: 'merge',
  stablehloselect: 'merge',
  stablehlodynamicslice: 'merge',
  stablehlodynamicupdateslice: 'merge',
  stablehloconcatenate: 'merge',

  // Attention
  multiheadattention: 'attention',
  attention: 'attention',
  embedlayernormalization: 'attention',
  skiplayernormalization: 'attention',
  biasgelu: 'attention',
  additiveattention: 'attention',

  // Recurrent
  lstm: 'recurrent',
  statespace: 'recurrent',
  transformerblock: 'recurrent',
  gru: 'recurrent',
  rnn: 'recurrent',
  unidirectionalsequencelstm: 'recurrent',
  bidirectionalsequencelstm: 'recurrent',
  bidirectionalsequencernn: 'recurrent',
  unidirectionalsequencernn: 'recurrent',
  svdf: 'recurrent',
  simplernn: 'recurrent',
  bidirectional: 'recurrent',
  timedistributed: 'recurrent',
  convlstm1d: 'recurrent',
  convlstm2d: 'recurrent',
  convlstm3d: 'recurrent',

  // Quantization
  quantizelinear: 'quantization',
  dequantizelinear: 'quantization',
  dynamicquantizelinear: 'quantization',
  quantize: 'quantization',
  dequantize: 'quantization',
  fakequant: 'quantization',

  // Logic / comparison
  equal: 'logic',
  notequal: 'logic',
  less: 'logic',
  lessequal: 'logic',
  lessorequal: 'logic',
  greater: 'logic',
  greaterequal: 'logic',
  greaterorequal: 'logic',
  logicaland: 'logic',
  logicalor: 'logic',
  logicalnot: 'logic',
};

// aten / torch namespace ops (TorchScript: "aten::op/Overload", ExecuTorch: "aten::op.Overload")
const ATEN_MAP: Record<string, OpCategory> = {
  // conv
  conv1d: 'conv',
  conv2d: 'conv',
  conv3d: 'conv',
  conv_transpose1d: 'conv',
  conv_transpose2d: 'conv',
  conv_transpose3d: 'conv',
  conv_depthwise3d: 'conv',
  convolution: 'conv',
  _convolution: 'conv',

  // math
  add: 'math',
  add_: 'math',
  sub: 'math',
  sub_: 'math',
  mul: 'math',
  mul_: 'math',
  div: 'math',
  div_: 'math',
  true_divide: 'math',
  floor_divide: 'math',
  remainder: 'math',
  fmod: 'math',
  pow: 'math',
  sqrt: 'math',
  rsqrt: 'math',
  exp: 'math',
  exp2: 'math',
  log: 'math',
  log2: 'math',
  log10: 'math',
  log1p: 'math',
  abs: 'math',
  neg: 'math',
  sign: 'math',
  ceil: 'math',
  floor: 'math',
  round: 'math',
  trunc: 'math',
  frac: 'math',
  clamp: 'math',
  clamp_: 'math',
  clip: 'math',
  clip_: 'math',
  reciprocal: 'math',
  addcmul: 'math',
  addcdiv: 'math',
  lerp: 'math',
  max: 'math',
  min: 'math',
  maximum: 'math',
  minimum: 'math',
  sin: 'math',
  cos: 'math',
  tan: 'math',
  asin: 'math',
  acos: 'math',
  atan: 'math',
  atan2: 'math',
  sinh: 'math',
  cosh: 'math',
  tanh: 'activation',
  sigmoid: 'activation',
  linear: 'math',
  matmul: 'math',
  mm: 'math',
  bmm: 'math',
  addmm: 'math',
  baddbmm: 'math',
  dot: 'math',
  mv: 'math',
  outer: 'math',
  einsum: 'math',
  cross: 'math',
  cumsum: 'reduction',
  cumprod: 'reduction',

  // activation
  relu: 'activation',
  relu_: 'activation',
  relu6: 'activation',
  leaky_relu: 'activation',
  leaky_relu_: 'activation',
  prelu: 'activation',
  elu: 'activation',
  elu_: 'activation',
  selu: 'activation',
  celu: 'activation',
  gelu: 'activation',
  silu: 'activation',
  silu_: 'activation',
  mish: 'activation',
  hardswish: 'activation',
  hardswish_: 'activation',
  hardsigmoid: 'activation',
  hardtanh: 'activation',
  hardtanh_: 'activation',
  softmax: 'activation',
  log_softmax: 'activation',
  softplus: 'activation',
  softshrink: 'activation',
  tanhshrink: 'activation',
  threshold: 'activation',
  threshold_: 'activation',

  // normalization
  batch_norm: 'normalization',
  layer_norm: 'normalization',
  group_norm: 'normalization',
  instance_norm: 'normalization',
  local_response_norm: 'normalization',
  rms_norm: 'normalization',
  norm: 'normalization',
  renorm: 'normalization',

  // pooling
  max_pool1d: 'pooling',
  max_pool2d: 'pooling',
  max_pool3d: 'pooling',
  avg_pool1d: 'pooling',
  avg_pool2d: 'pooling',
  avg_pool3d: 'pooling',
  adaptive_max_pool1d: 'pooling',
  adaptive_max_pool2d: 'pooling',
  adaptive_max_pool3d: 'pooling',
  adaptive_avg_pool1d: 'pooling',
  adaptive_avg_pool2d: 'pooling',
  adaptive_avg_pool3d: 'pooling',
  fractional_max_pool2d: 'pooling',
  fractional_max_pool3d: 'pooling',
  lp_pool1d: 'pooling',
  lp_pool2d: 'pooling',

  // reshape
  reshape: 'reshape',
  view: 'reshape',
  flatten: 'reshape',
  squeeze: 'reshape',
  squeeze_: 'reshape',
  unsqueeze: 'reshape',
  unsqueeze_: 'reshape',
  transpose: 'reshape',
  permute: 'reshape',
  contiguous: 'reshape',
  expand: 'reshape',
  expand_as: 'reshape',
  repeat: 'reshape',
  reshape_as: 'reshape',
  as_strided: 'reshape',
  narrow: 'reshape',
  slice: 'reshape',
  select: 'reshape',
  index_select: 'reshape',
  pad: 'reshape',
  constant_pad_nd: 'reshape',
  reflection_pad1d: 'reshape',
  reflection_pad2d: 'reshape',
  replication_pad1d: 'reshape',
  replication_pad2d: 'reshape',
  replication_pad3d: 'reshape',
  upsample_nearest1d: 'reshape',
  upsample_nearest2d: 'reshape',
  upsample_nearest3d: 'reshape',
  upsample_bilinear2d: 'reshape',
  upsample_bicubic2d: 'reshape',
  upsample_linear1d: 'reshape',
  upsample_trilinear3d: 'reshape',
  interpolate: 'reshape',
  pixel_shuffle: 'reshape',
  pixel_unshuffle: 'reshape',
  roll: 'reshape',
  rot90: 'reshape',
  flip: 'reshape',
  fliplr: 'reshape',
  flipud: 'reshape',

  // reduction
  sum: 'reduction',
  mean: 'reduction',
  prod: 'reduction',
  std: 'reduction',
  var: 'reduction',
  amax: 'reduction',
  amin: 'reduction',
  argmax: 'reduction',
  argmin: 'reduction',
  topk: 'reduction',
  sort: 'reduction',
  argsort: 'reduction',
  all: 'reduction',
  any: 'reduction',
  count_nonzero: 'reduction',
  logsumexp: 'reduction',

  // merge / indexing
  cat: 'merge',
  stack: 'merge',
  hstack: 'merge',
  vstack: 'merge',
  dstack: 'merge',
  chunk: 'merge',
  split: 'merge',
  tensor_split: 'merge',
  unbind: 'merge',
  gather: 'merge',
  scatter: 'merge',
  scatter_add: 'merge',
  index: 'merge',
  index_put: 'merge',
  index_put_: 'merge',
  masked_select: 'merge',
  masked_fill: 'merge',
  masked_fill_: 'merge',
  where: 'merge',
  nonzero: 'merge',
  unique: 'merge',
  embedding: 'merge',
  embedding_bag: 'merge',

  // recurrent
  lstm: 'recurrent',
  lstm_cell: 'recurrent',
  gru: 'recurrent',
  gru_cell: 'recurrent',
  rnn_tanh: 'recurrent',
  rnn_relu: 'recurrent',
  rnn_tanh_cell: 'recurrent',
  rnn_relu_cell: 'recurrent',

  // attention
  scaled_dot_product_attention: 'attention',
  multi_head_attention_forward: 'attention',

  // quantization
  quantize_per_tensor: 'quantization',
  quantize_per_channel: 'quantization',
  dequantize: 'quantization',
  fake_quantize_per_tensor_affine: 'quantization',
  fake_quantize_per_channel_affine: 'quantization',

  // logic
  eq: 'logic',
  ne: 'logic',
  lt: 'logic',
  le: 'logic',
  gt: 'logic',
  ge: 'logic',
  logical_and: 'logic',
  logical_or: 'logic',
  logical_not: 'logic',
  logical_xor: 'logic',
  isnan: 'logic',
  isinf: 'logic',
  isfinite: 'logic',

  // constant / creation
  zeros: 'constant',
  zeros_like: 'constant',
  ones: 'constant',
  ones_like: 'constant',
  full: 'constant',
  full_like: 'constant',
  arange: 'constant',
  linspace: 'constant',
  eye: 'constant',
  empty: 'constant',
  empty_like: 'constant',
  rand: 'constant',
  randn: 'constant',
  randint: 'constant',
  tensor: 'constant',
  scalar_tensor: 'constant',
  as_tensor: 'constant',
  fill_: 'constant',
  zero_: 'constant',

  // regularization
  dropout: 'activation',
  dropout_: 'activation',
  alpha_dropout: 'activation',
  feature_alpha_dropout: 'activation',

  // shape queries
  size: 'reshape',
  sizes: 'reshape',
  dim: 'reshape',
  ndim: 'reshape',
  stride: 'reshape',
  t: 'reshape',
  to: 'reshape',
  type_as: 'reshape',
  clone: 'reshape',
  detach: 'reshape',
  detach_: 'reshape',
  copy_: 'reshape',

  // count / length
  numel: 'reduction',
  len: 'reduction',
  __len__: 'reduction',
  item: 'reduction',

  // indexing
  __getitem__: 'merge',
  __setitem__: 'merge',
};

/** Extracts the base op name from an aten namespace op (e.g. "aten::add.int" -> "add").
 * Returns null for non-aten ops.
 */
export function opBase(opType: string): string | null {
  const sep = opType.indexOf('::');
  if (sep === -1) return null;
  const after = opType.slice(sep + 2);
  const end = after.search(/[./]/);
  return end === -1 ? after : after.slice(0, end);
}

function atenCategory(opType: string): OpCategory | null {
  const base = opBase(opType);
  return base != null ? (ATEN_MAP[base] ?? null) : null;
}

export function opCategory(opType: string): OpCategory {
  const normalized = opType.toLowerCase().replace(/_/g, '');
  return CATEGORY_MAP[normalized] ?? atenCategory(opType) ?? 'unknown';
}
